import { useState } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout, Menu, Button, Tag, Alert, Space, Segmented, Typography, Dropdown, Result, Grid, Drawer } from 'antd';
import {
  LockOutlined,
  CodeOutlined,
  DashboardOutlined,
  DesktopOutlined,
  TeamOutlined,
  FileTextOutlined,
  HddOutlined,
  DatabaseOutlined,
  UserOutlined,
  LogoutOutlined,
  BulbOutlined,
  BulbFilled,
  DownOutlined,
  CloudDownloadOutlined,
  ThunderboltOutlined,
  MenuOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { api, HttpError, User, isAdminRole } from './api';
import { Logo } from './components/Logo';
import { ChatWidget } from './components/ChatWidget';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Console } from './pages/Console';
import { Firmware } from './pages/Firmware';
import { SessionEditor } from './pages/SessionEditor';
import { Settings } from './pages/Settings';
import { Disks } from './pages/Disks';
import { DiskTimeline } from './pages/DiskTimeline';
import { FlashHistory } from './pages/FlashHistory';
import { Devices } from './pages/Devices';
import { DeviceLogPage } from './pages/DeviceLogPage';
import { Users } from './pages/Users';
import { AuditLog } from './pages/AuditLog';
import { Account } from './pages/Account';
import { t, getLocale, setLocale, Locale } from './i18n';
import { useThemeMode } from './theme';
import { HintTooltip as Tooltip } from './components/HintTooltip';

const { Header, Sider, Content, Footer } = Layout;


/** Shared brand footer: copyright + running version, theme-aware. */
function BrandFooter({
  mode,
  serverInfo,
}: {
  mode: 'light' | 'dark';
  serverInfo?: { version: string; commit: string };
}) {
  const muted = mode === 'dark' ? '#8b95ad' : '#6b7488';
  return (
    <Footer
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        // Thin pinned bar (not part of the scrolling content), so keep it short.
        padding: '8px 20px',
        lineHeight: 1.2,
        fontSize: 12,
        color: muted,
        flexShrink: 0,
        background: mode === 'dark' ? '#1f1f1f' : '#fff',
        borderTop: `1px solid ${mode === 'dark' ? '#303030' : '#eee'}`,
      }}
    >
      <span>© 2026 DDiskSatellite</span>
      {serverInfo && (
        <>
          <span aria-hidden>·</span>
          <Tooltip title={`commit ${serverInfo.commit}`}>
            <span>v{serverInfo.version}</span>
          </Tooltip>
          <span aria-hidden>·</span>
          <Tooltip title={`commit ${serverInfo.commit}`}>
            <span>{serverInfo.commit}</span>
          </Tooltip>
        </>
      )}
    </Footer>
  );
}

/** Wrap a not-yet-migrated page so the scoped legacy CSS applies to it. */
const legacy = (node: React.ReactNode) => <div className="legacy-page">{node}</div>;

export function App() {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (e) {
        if (e instanceof HttpError && e.status === 401) return null;
        throw e;
      }
    },
  });

  if (me.isLoading) return <div style={{ padding: 40 }}>{t('loadingEllipsis')}</div>;
  const user = me.data?.user;

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Forced password change was removed: a freshly-created user renders the
  // normal Shell immediately. Self-service password change lives on the Account
  // page (POST /api/auth/password) and is still available.
  return (
    <Shell user={user} impersonating={me.data?.impersonating} adminEmail={me.data?.adminEmail} />
  );
}

function Shell({
  user,
  impersonating,
  adminEmail,
}: {
  user: User;
  impersonating?: boolean;
  adminEmail?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { mode, toggle } = useThemeMode();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [, force] = useState(0);
  const isAdmin = isAdminRole(user.role);
  const isSuper = user.role === 'superadmin';

  // Responsive shell: below `lg` (992px) the fixed Sider is hidden and the same
  // menu is exposed through a left Drawer opened by the top-bar hamburger.
  const screens = Grid.useBreakpoint();
  const isNarrow = !screens.lg;
  const isXs = !screens.sm;

  const quota = useQuery({
    queryKey: ['my-quota'],
    queryFn: () => api.myQuota(),
    enabled: user.role === 'client',
    refetchInterval: 10_000,
  });
  const serverInfo = useQuery({
    queryKey: ['server-info'],
    queryFn: api.getServerInfo,
    staleTime: 60_000,
  });

  const navItems = [
    ...(isAdmin
      ? [{ key: '/dashboard', icon: <DashboardOutlined />, label: t('navDashboard') }]
      : []),
    {
      key: '/disks',
      icon: <DatabaseOutlined />,
      label: t('navDisks'),
    },
    {
      key: '/devices',
      icon: <DesktopOutlined />,
      label: t('navDevices'),
    },
    {
      key: '/flash-history',
      icon: <ThunderboltOutlined />,
      label: t('navFlashHistory'),
    },
    ...(isAdmin
      ? [
          { key: '/users', icon: <TeamOutlined />, label: t('navUsers') },
          { key: '/backups', icon: <HddOutlined />, label: t('navBackups') },
          { key: '/logs', icon: <FileTextOutlined />, label: t('navAudit') },
        ]
      : []),
    ...(isSuper
      ? [
          { key: '/firmware', icon: <CloudDownloadOutlined />, label: t('navFirmware') },
          { key: '/console', icon: <CodeOutlined />, label: t('navConsole') },
          { key: '/settings', icon: <SettingOutlined />, label: t('navSettings') },
        ]
      : []),
  ];

  // Which nav item is active. The disk editor lives under /sessions and belongs
  // to the Disks workspace; /backups is the disk backup/history browser.
  const selectedKey =
    location.pathname.startsWith('/firmware')
      ? '/firmware'
      : location.pathname.startsWith('/settings')
      ? '/settings'
      : location.pathname.startsWith('/console')
      ? '/console'
      : location.pathname.startsWith('/dashboard')
      ? '/dashboard'
      : location.pathname.startsWith('/flash-history')
      ? '/flash-history'
      : location.pathname.startsWith('/users')
      ? '/users'
      : location.pathname.startsWith('/backups')
        ? '/backups'
        : location.pathname.startsWith('/disks') || location.pathname.startsWith('/sessions')
          ? '/disks'
          : location.pathname.startsWith('/logs')
            ? '/logs'
            : location.pathname.startsWith('/account')
              ? '/account'
              : '/devices';

  const onLogout = async () => {
    await api.logout();
    // Drop ALL cached queries so a different user logging in on the same browser
    // never briefly sees the previous user's data (role-agnostic keys like
    // ['users'], ['stats-*'], ['flash-history-page'] would otherwise flash through).
    qc.clear();
    qc.setQueryData(['me'], null);
    navigate('/login');
  };

  const returnToAdmin = async () => {
    await api.impersonationReturn();
    window.location.href = '/users';
  };

  // One Menu definition (items + handler) drives BOTH the desktop Sider and the
  // mobile Drawer. On the narrow Drawer, a tap also closes the drawer.
  const navMenu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      items={navItems}
      onClick={(e) => {
        navigate(e.key);
        setDrawerOpen(false);
      }}
    />
  );

  const siderLogo = (
    <div
      style={{
        height: 56,
        margin: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed && !isNarrow ? 'center' : 'flex-start',
        paddingLeft: collapsed && !isNarrow ? 0 : 4,
        overflow: 'hidden',
      }}
    >
      <Logo size={34} showWordmark={isNarrow || !collapsed} wordmarkColor="#e8ecf4" />
    </div>
  );

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden', background: 'transparent' }}>
      {user.role === 'client' && !impersonating && <ClientBlockOverlay />}
      {!isNarrow && (
        <Sider
          theme="dark"
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={{ position: 'relative' }}
        >
          {siderLogo}
          {navMenu}
        </Sider>
      )}

      {/* Mobile navigation: same menu inside a left Drawer */}
      <Drawer
        placement="left"
        open={isNarrow && drawerOpen}
        onClose={() => setDrawerOpen(false)}
        closable={false}
        width={240}
        styles={{ body: { padding: 0, background: '#001529' }, header: { display: 'none' } }}
      >
        {siderLogo}
        {navMenu}
      </Drawer>

      <Layout style={{ background: 'transparent', minWidth: 0 }}>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 8,
            gap: 12,
            height: 'auto',
            minHeight: 64,
            paddingBlock: 8,
            paddingInline: isNarrow ? 12 : 20,
            background: mode === 'dark' ? '#1f1f1f' : '#fff',
            borderBottom: `1px solid ${mode === 'dark' ? '#303030' : '#eee'}`,
          }}
        >
          {isNarrow && (
            <Button
              type="text"
              aria-label={t('navOpenMenu')}
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
            />
          )}
          <Typography.Title level={4} style={{ margin: 0 }}>
            {navItems.find((i) => i.key === selectedKey)?.label ??
              (location.pathname.startsWith('/account') ? t('navAccount') : null)}
          </Typography.Title>
          <div style={{ flex: 1, minWidth: 0 }} />
          <Space size={12} wrap>
            {/* Language toggle is available to everyone (clients too, and while an
                admin is impersonating a client) — the client UI is fully bilingual,
                so it must not be locked to English. */}
            <Segmented
              size="small"
              value={getLocale()}
              onChange={(v) => {
                setLocale(v as Locale);
                force((x) => x + 1);
              }}
              options={[
                { label: 'EN', value: 'en' },
                { label: 'RU', value: 'ru' },
              ]}
            />
            <Tooltip title={mode === 'dark' ? t('themeToLight') : t('themeToDark')}>
              <Button
                type="text"
                icon={mode === 'dark' ? <BulbFilled /> : <BulbOutlined />}
                onClick={toggle}
              />
            </Tooltip>
            {user.role === 'client' && quota.data && (
              <Tag color={quota.data.available > 0 ? 'green' : 'orange'}>
                {t('quotaBalance')}: {quota.data.available}
              </Tag>
            )}
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'role',
                    disabled: true,
                    label: (
                      <Tag color={isAdmin ? 'blue' : 'default'} style={{ margin: 0 }}>
                        {user.role}
                      </Tag>
                    ),
                  },
                  { type: 'divider' },
                  { key: 'account', icon: <UserOutlined />, label: t('navAccount') },
                  { key: 'logout', icon: <LogoutOutlined />, danger: true, label: t('navLogout') },
                ],
                onClick: ({ key }) => {
                  if (key === 'account') navigate('/account');
                  else if (key === 'logout') onLogout();
                },
              }}
            >
              <Button type="text">
                <Space size={6}>
                  <UserOutlined />
                  {!isXs && <span>{user.username}</span>}
                  <DownOutlined style={{ fontSize: 10, opacity: 0.65 }} />
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header>

        {/* Only the Content scrolls; the Sider and Header stay put. flex+minHeight:0
            lets it shrink inside the 100vh column so its own overflow kicks in. */}
        <Content
          style={{
            margin: isNarrow ? '12px 8px' : 20,
            minWidth: 0,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {impersonating && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('impersonationBanner', { email: user.username })}
              description={adminEmail ? `${t('impersonationReturn')} → ${adminEmail}` : undefined}
              action={
                <Button size="small" onClick={returnToAdmin}>
                  {t('impersonationReturn')}
                </Button>
              }
            />
          )}
          <RecoveryCodesBanner />
          <Routes>
            {/* Admins/superadmins land on the Dashboard; clients (no Dashboard)
                land on their Disks workspace. */}
            <Route path="/" element={<Navigate to={isAdmin ? '/dashboard' : '/disks'} replace />} />
            <Route path="/login" element={<Navigate to={isAdmin ? '/dashboard' : '/disks'} replace />} />
            <Route path="/disks" element={<Disks user={user} />} />
            <Route path="/devices" element={<Devices user={user} />} />
            <Route path="/flash-history" element={<FlashHistory user={user} />} />
            <Route path="/sessions" element={<Navigate to="/disks" replace />} />
            <Route path="/sessions/:id" element={legacy(<SessionEditor user={user} />)} />
            <Route path="/account" element={<Account user={user} />} />
            {isAdmin && (
              <>
                <Route path="/dashboard" element={<Dashboard />} />
                {isSuper && <Route path="/console" element={<Console />} />}
                {isSuper && <Route path="/firmware" element={<Firmware />} />}
                {isSuper && <Route path="/settings" element={<Settings />} />}
                <Route path="/users" element={<Users />} />
                <Route path="/quotas" element={<Navigate to="/users" replace />} />
                <Route path="/backups" element={<DiskTimeline />} />
                <Route path="/devices/:id/log" element={<DeviceLogPage />} />
                <Route path="/logs" element={<AuditLog />} />
                <Route path="/audit" element={<Navigate to="/logs" replace />} />
              </>
            )}
            <Route path="*" element={<Navigate to="/disks" replace />} />
          </Routes>
        </Content>
        {/* Pinned to the bottom of the viewport in its own thin bar — a sibling of
            the scrolling Content, not the tail of each page's blocks. */}
        <BrandFooter mode={mode} serverInfo={serverInfo.data} />
      </Layout>
      {/* The admin feedback/chat widget is hidden system-wide when a superadmin
          disables it in Settings (default on). */}
      {(serverInfo.data?.feedbackEnabled ?? true) && (
        <ChatWidget user={user} impersonating={impersonating} />
      )}
    </Layout>
  );
}

/** Full-screen lock shown to a CLIENT when an admin is working under their
 * account with the "block" option. Polls the status; unlocks automatically when
 * the admin returns. Rendered only for a client's own session (never the admin's
 * impersonated view). */
function ClientBlockOverlay() {
  const q = useQuery({
    queryKey: ['impersonation-status'],
    queryFn: api.impersonationStatus,
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });
  if (!q.data?.blocked) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(8, 11, 22, 0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backdropFilter: 'blur(2px)',
      }}
    >
      <Result
        icon={<LockOutlined style={{ color: '#fbbf24' }} />}
        title={t('impersonateBlockedTitle')}
        subTitle={t('impersonateBlockedBody', { name: q.data.adminName || 'admin' })}
        style={{ maxWidth: 560 }}
      />
    </div>
  );
}

const RECOVERY_BANNER_DISMISS_KEY = 'ddisk.recovery-banner.dismissed';

function RecoveryCodesBanner() {
  const navigate = useNavigate();
  const totp = useQuery({ queryKey: ['totp-status'], queryFn: api.totpStatus, staleTime: 60_000 });
  const count = useQuery({
    queryKey: ['recovery-codes-count'],
    queryFn: api.recoveryCodesCount,
    enabled: totp.data?.enabled === true,
    staleTime: 60_000,
  });
  const [dismissed, setDismissed] = useState(
    typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(RECOVERY_BANNER_DISMISS_KEY) === '1',
  );
  if (dismissed) return null;
  if (!totp.data?.enabled) return null;
  if (!count.data) return null;
  if (count.data.remaining >= 3) return null;
  return (
    <Alert
      type="warning"
      showIcon
      closable
      onClose={() => {
        sessionStorage.setItem(RECOVERY_BANNER_DISMISS_KEY, '1');
        setDismissed(true);
      }}
      style={{ marginBottom: 16 }}
      message={t('recoveryBannerTitle')}
      description={t('recoveryBannerBody', { remaining: count.data.remaining })}
      action={
        <Button size="small" onClick={() => navigate('/account')}>
          {t('recoveryBannerCta')}
        </Button>
      }
    />
  );
}
